import type { PartnerDef } from "../schema";

/**
 * 1社目 Blue Rocket（サービスブランド「みんなのドライバー」系）。
 * 要件定義管理表『Blue Rocket 要望』列の差分のみ。「変更なし」項目は default を継承。
 * 未確定・不整合の値は現状のまま採用し flags に注記（変更依頼時に別タスクで修正）。
 */
export const blueRocket: PartnerDef = {
  // 面の在り処（環境ごと）。**環境で変わるのはここだけ**。
  // prod: OEM 白ラベルのため提携先ドメイン配下。stg: 白ラベル不要のため ROXX 側のホストでよい。
  // ★Cloudflare Pages プロジェクト＋カスタムドメイン＋DNS は infra 操作。prod はプレースホルダ。
  delivery: {
    // stg は白ラベル不要（提携先のエンドユーザーは触らない）。**本番と同じホストを書くと
    // stg のメール内リンクや遷移先が本番を指す**ため、stg の実際の配信先を書く。
    // portal は Cloudflare Pages の OEM プロジェクト（カスタムドメインは本番のみ付与）。
    stg: {
      // 提携先の stg 独自ドメイン（AI-INT-2222 Phase 3 STG で配線・2026-08-04）。
      // ai-interview STG 共有 LB が {app,company,viewer}.stg.ai-interview.minnanodriver.com を配信する。
      // 旧値は検証用 run.app（`ai-company-oem-blue-rocket-...`）だったが、独自ドメイン固定 + OEM Cloud Run の
      // ingress INTERNAL_LOAD_BALANCER 化で run.app 直アクセスは 404。案内メールのログイン URL もこの独自
      // ドメインから組む（提携先のエンドユーザーの遷移先＝提携先ドメインに帰属）。
      adminHost: "company.stg.ai-interview.minnanodriver.com",
      // viewer / exam の stg ホストも stg 独自ドメインに更新（2026-08-04 配線・疎通確認済）。
      viewerHost: "viewer.stg.ai-interview.minnanodriver.com",
      // Cloudflare Pages の OEM プロジェクトの **`stg` ブランチのエイリアス**。
      // プロジェクトの素のエイリアス（`company-auth-br-oem.pages.dev`）は**本番デプロイ**を
      // 指すため、そこを stg の値にすると stg のメール内リンク・遷移先が本番の配信に着く
      // （カスタムドメイン付与前は、その裸の URL が stg 経由で露出し続ける）。
      // 既定テナントが `stg.company-auth.pages.dev` を使うのと同じ形に揃える。
      portalHost: "stg.company-auth-br-oem.pages.dev",
      examHost: "app.stg.ai-interview.minnanodriver.com",
      assetBaseUrl: "https://assets.oem-br.stg.zcareer.com",
      // 送信元は **stg の送信基盤（SES）で DKIM 認証済みのドメイン**にする。
      // stg の認証済みドメインは `oem-br.stg.zcareer.com` 配下（企業向け=company / 候補者向け=
      // ai-interview。統括 tasks12・civitus#435）。
      //
      // **提携先の本番ドメイン（blue-rocket.jp / ai-interview.minnanodriver.com）を stg の
      // 送信元にしてはならない。** 認証を満たさず送れないうえ、検証環境から提携先を名乗ることに
      // なる。従来は送信元がブランド側（環境非依存）にあったためここが本番の値になっており、
      // stg で SES 経路を検証できなかった（本番で初めて実行され company-auth-infra#247 の障害）。
      accountIssueFrom: "admin@company.oem-br.stg.zcareer.com",
      completionNoticeFrom: "admin@company.oem-br.stg.zcareer.com",
      remindNoticeFrom: "noreply@ai-interview.oem-br.stg.zcareer.com",
    },
    prod: {
      adminHost: "company.ai-interview.minnanodriver.com",
      viewerHost: "viewer.ai-interview.minnanodriver.com",
      portalHost: "portal.ai-interview.minnanodriver.com",
      examHost: "app.ai-interview.minnanodriver.com",
      assetBaseUrl: "https://assets.ai-interview.minnanodriver.com",
      // 本番の送信元。要件定義管理表 行11・12（企業向け＝blue-rocket.jp）／行24（候補者向け＝
      // ai-interview.minnanodriver.com）。使い分けは先方の意図的な要望。
      accountIssueFrom: "admin@blue-rocket.jp",
      completionNoticeFrom: "admin@blue-rocket.jp",
      remindNoticeFrom: "noreply@ai-interview.minnanodriver.com",
    },
  },
  // 提携先の提示の同一性（環境に依らない）。面の在り処は上の delivery に書く。
  brand: {
    company: {
      brand: {
        // メール・画面に出すサービス名は「みんなのドライバー」で先方確定
        // （2026-07-21 追加確認_0721 #1）→ SSOT として供給側で明示する。
        // 従来は null で、消費側（company-auth・ai-interview）が提携先ごとの表示名を各自
        // ハードコードしてフォールバックしていた。その消費側フォールバックは不要になる。
        serviceName: "みんなのドライバー",
        serviceNameLogo: "assets/br/minnano-driver.svg", // 正方形マーク
        logo: "assets/br/minna-no-mensetsu.svg", // 横長ワードマーク
        logoPng: "assets/br/minna-no-mensetsu.png", // 横長ワードマークの PNG 版（メール本文用。SVG 非対応クライアント対策）
        favicon: "assets/br/minnano-driver.svg",
        copyright: "© みんなのドライバー",
        operatingCompany: "© Blue Rocket",
      },
      design: {
        buttonColor: "#3087EF",
        helpUrl: "https://blog.minnanodriver.com/help/",
      },
      domain: {
        // 面の在り処（adminHost / viewerHost / portalHost）は delivery に書く。
        // termsHost は提携先自身のサイトで環境で変わらないため、ここに残す。
        termsHost: "minnanodriver.com", // 2026-07-10 先方 URL 更新: 企業向け規約は先方サイト minnanodriver.com/companyinterviewterms に掲出（先方管理・弊社はリンク）
      },
      legal: {
        // 企業向け利用規約は先方サイトに掲出（2026-07-10 先方 URL 更新・先方管理・弊社はリンク）
        termsUrl: "https://minnanodriver.com/companyinterviewterms",
        termsName: "【企業向け】20260525_AI面接官（OEM）利用規約",
        privacyPolicyUrl: "https://blog.minnanodriver.com/privacy-policy/",
      },
      // 送信元（accountIssueFrom / completionNoticeFrom）は delivery.<env> に置く。
      // 環境で変わる（送信ドメイン認証は環境ごとの送信基盤に対して行う）ため。
    },
    candidate: {
      brand: {
        // 候補者向けサービス名称は先方記入なし → null（default の "Zキャリア" を継承しない）
        serviceName: null,
        copyright: "©Blue Rocket",
        operatingCompany: "https://blue-rocket.jp/",
      },
      design: {
        buttonColor: "#3087EF",
        helpUrl: "https://blog.minnanodriver.com/ai-interview-guide/", // 2026-07-21 先方確認（追加確認_0721 #4）: 候補者受験画面の操作ガイド/FAQ リンク
      },
      legal: {
        termsName: "【候補者向け】20260525_AI面接官（OEM）利用規約",
        termsUrl: "https://minnanodriver.com/interviewterms", // 2026-07-10 先方 URL 更新: 候補者向け規約は先方サイトに掲出（先方管理・弊社はリンク）
      },
      // 送信元（remindNoticeFrom）は delivery.<env> に置く。理由は company 側と同じ。
    },
  },
  flags: {
    "company.brand.serviceName":
      "テキスト表記が先方未明示（画像ロゴのみ受領）。ワードマークの aria-label は「みんなの面接」。2026-07-21 先方回答（追加確認_0721 #1）: メール露出のサービス名は『みんなのドライバー』を希望（ただしメール文面を確認してから最終判断）。現状 serviceName=null → 消費側で OemTenant.displayName='みんなのドライバー' にフォールバック済みで挙動は既に一致。先方の文面確認が取れたら serviceName を明示設定に切替（挙動不変）。",
    assets:
      "受領アセットは SVG。serviceNameLogo/favicon=minnano-driver.svg（正方形マーク）、logo=minna-no-mensetsu.svg（横長ワードマーク＝「みんなの面接」）。メール本文用は SVG 非対応クライアントが多いため、logo(SVG) と同一内容を rsvg-convert でラスタライズした logoPng=minna-no-mensetsu.png（786x263・「みんなの面接」）を追加配信（消費側はメールで logoPng を使う）。SVG(SSOT)→PNG の決定論的自動生成は #726。",
    "company.legal.termsUrl":
      "企業向け利用規約は先方サイト https://minnanodriver.com/companyinterviewterms に掲出（2026-07-10 先方 URL 更新・先方管理・弊社はリンクのみ）。当初『ROXX 配信・app.ai-interview.minnanodriver.com』としていたが本更新で上書き。受験画面アプリ本体は引き続き app.ai-interview.minnanodriver.com＝ROXX 配信。",
    "company.legal.privacyPolicyUrl":
      "2026-07-21 先方確認（追加確認_0721 #5）で https://blog.minnanodriver.com/privacy-policy/ に更新（旧: https://nihon-driver.jp/terms）。CSV の依頼は候補者アンケートの PP だが、供給スキーマ上 privacyPolicyUrl は company.legal のみ（候補者は build 時にこの値を注入）＝共有フィールド。company footer / メール PP も本 URL に統一される。",
    "candidate.brand.serviceName":
      "候補者向けサービス名称テキストは先方記入なし（受験画面表記なし・利用規約内のみ）。zcareer を継承しないため null。",
    "candidate.mail.remindNoticeFrom":
      "2026-07-21 先方回答（追加確認_0721 #2）: support@blue-rocket.jp を希望（暫定案 admin@blue-rocket.jp から変更）。ただし blue-rocket.jp からの送信は SendGrid ドメイン認証（SPF/DKIM）が前提で、送信ドメイン切替は infra/供給側タスク。From 現状維持方針のため値は noreply@ai-interview.minnanodriver.com のまま保留し、ドメイン認証完了後に support@blue-rocket.jp へ更新する。",
    "delivery.stg": [
      "2026-07-30: 面の在り処を brand から delivery（環境別）へ分離した際に、stg の値を実際の stg 配信先へ直した。",
      "2026-07-31: portalHost を stg.company-auth-br-oem.pages.dev（Cloudflare Pages の OEM プロジェクトの stg ブランチのエイリアス）へ直した。従来は company-auth-br-oem.pages.dev（プロジェクトの素のエイリアス＝本番デプロイ）を指しており、stg のメール内リンク・遷移先が本番の配信に着いていた（カスタムドメイン付与前は、その裸の URL が stg 経由で露出し続ける）。既定テナントが stg.company-auth.pages.dev を使うのと同じ形に揃えた。",
      "2026-07-31: adminHost を ai-company-oem-blue-rocket-4c3pkdjwwa-an.a.run.app（提携先向け AI面接官の検証用サービス）へ直した。従来は company.ai-interview.stg.zcareer.com（既定テナントの面）を指しており、案内メールのログイン URL がそこから組まれるため、提携先のエンドユーザーを既定テナントの面へ誘導していた。",
      "未確認: viewerHost / examHost の stg ホストは ai-interview 側の配信で確認できていないため、現状値（本番と同じ）のまま置いた。ai-interview 側に確認して直す。",
    ].join(""),
    policy:
      "上記は要件定義管理表の値を現状のまま採用。先方から変更依頼が来た時点で別タスクで修正する（統括方針）。",
  },
};
